/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Avatar,
    Chip,
    alpha,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import DemoBlur from 'src/components/DemoBlur';
import useDemoMode from 'src/hooks/useDemoMode';
import MetricLineChart from './MetricLineChart';
import SourceLeadPopup from './SourceLeadPopup';

const TABLE_NAME = 'Practitioner Table';


// Column configuration for consistent Chip styling
const COLUMN_CONFIG = {
    leads: { color: '#3b82f6' },
    consultationsBooked: { color: '#6366f1' },
    consultationsAttended: { color: '#8b5cf6' },
    conversions: { color: '#10b981' },
    revenue: { color: '#10b981' },
    consultations: { color: '#6366f1' },
    showUpRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
    conversionRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
};

const RANK_COLORS = {
    1: { bg: '#fef3c7', text: '#d97706', border: '#fcd34d' },
    2: { bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
    3: { bg: '#fed7aa', text: '#c2410c', border: '#fdba74' },
    default: { bg: '#f1f5f9', text: '#94a3b8', border: '#e2e8f0' },
};

// Map column fields to lead statuses for popup filtering
const COLUMN_TO_STATUS = {
    leads: 'All',
    consultationsBooked: 'CONSULTATION_BOOKED',
    consultationsAttended: 'CONSULTATION_ATTENDED',
    conversions: 'TREATMENT_STARTED',
    consultations: 'All',
};

const COLUMN_STATUS_LABELS = {
    leads: 'All Leads',
    consultationsBooked: 'Consultation Booked',
    consultationsAttended: 'Consultation Attended',
    conversions: 'Treatment Started',
    consultations: 'All Leads',
};

export default function PractitionersTable({
    practitioners,
    title,
    showSearch,
    onPractitionerClick,
    practiceId,
    dateRange,
}) {
    const buildExportName = useReportExportFilename();
    const [demoMode] = useDemoMode();
    const [view, setView] = useState('table');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'revenue', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Popup state
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        assignedTo: null,
        status: 'All',
        statusLabel: '',
        title: '',
    });

    const handleCellClick = useCallback((practitioner, field) => {
        const value = practitioner[field];
        if (!value || value <= 0 || !practiceId) return;
        const status = COLUMN_TO_STATUS[field];
        if (!status) return;
        setPopupConfig({
            assignedTo: practitioner.userId || practitioner.id,
            status,
            statusLabel: COLUMN_STATUS_LABELS[field],
            title: `${demoMode ? 'Practitioner' : practitioner.name} — ${COLUMN_STATUS_LABELS[field]}`,
        });
        setPopupOpen(true);
    }, [practiceId, demoMode]);

    const handleClosePopup = useCallback(() => {
        setPopupOpen(false);
    }, []);

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const getRankColor = (rank) => RANK_COLORS[rank] || RANK_COLORS.default;

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(1)}K`;
        }
        return `£${value}`;
    };

    // Process data with ranks based on current sort
    const processedData = useMemo(() => {
        const sorted = [...practitioners].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
        return sorted.map((p, idx) => ({
            ...p,
            rank: idx + 1,
        }));
    }, [practitioners]);

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        return processedData.filter((row) =>
            row.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [processedData, searchQuery]);

    // Build chart data from the same filteredData the table uses.
    // Series are filtered by hiddenColumns so hiding a column also removes it
    // from the graph (mirrors the same approach as SourceStatusTable).
    const chartData = useMemo(() => {
        const hasEnhancedData = filteredData.some((p) => p.leads !== undefined);
        const categories = filteredData.map((p) => p.name || '');
        let allSeries;
        if (hasEnhancedData) {
            allSeries = [
                { name: 'Leads', field: 'leads', data: filteredData.map((p) => p.leads || 0), color: COLUMN_CONFIG.leads.color },
                { name: 'Booked', field: 'consultationsBooked', data: filteredData.map((p) => p.consultationsBooked || 0), color: COLUMN_CONFIG.consultationsBooked.color },
                { name: 'Attended', field: 'consultationsAttended', data: filteredData.map((p) => p.consultationsAttended || 0), color: COLUMN_CONFIG.consultationsAttended.color },
                { name: 'Converted', field: 'conversions', data: filteredData.map((p) => p.conversions || 0), color: COLUMN_CONFIG.conversions.color },
            ];
        } else {
            allSeries = [
                { name: 'Consultations', field: 'consultations', data: filteredData.map((p) => p.consultations || 0), color: COLUMN_CONFIG.consultations.color },
                { name: 'Conversions', field: 'conversions', data: filteredData.map((p) => p.conversions || 0), color: COLUMN_CONFIG.conversions.color },
            ];
        }
        const series = allSeries
            .filter((s) => !hiddenColumns.includes(s.field))
            .map(({ name, data, color }) => ({ name, data, color }));
        return { categories, series };
    }, [filteredData, hiddenColumns]);

    // Build columns - show enhanced data when available
    const columns = useMemo(() => {
        const hasEnhancedData = processedData.some((p) => p.leads !== undefined);

        const baseColumns = [
            {
                field: 'rank',
                headerName: 'Rank',
                width: 70,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const rankColor = getRankColor(params.value);
                    return (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '14px',
                                bgcolor: rankColor.bg,
                                color: rankColor.text,
                                border: `1px solid ${rankColor.border}`,
                            }}
                        >
                            {params.value}
                        </Box>
                    );
                },
            },
            {
                field: 'name',
                headerName: 'Team Member',
                flex: 1.2,
                minWidth: 150,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                        <DemoBlur>
                        <Avatar
                            src={params.row.avatar}
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: '#e2e8f0',
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '13px',
                            }}
                        >
                            {getInitials(params.value)}
                        </Avatar>
                        </DemoBlur>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                            <DemoBlur>{params.value}</DemoBlur>
                        </Typography>
                    </Box>
                ),
            },
        ];

        if (hasEnhancedData) {
            // Enhanced columns with full funnel data - using Chip style
            return [
                ...baseColumns,
                {
                    field: 'leads',
                    headerName: 'Leads',
                    width: 80,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const value = params.value || 0;
                        const config = COLUMN_CONFIG.leads;
                        return (
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'leads'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                    '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'consultationsBooked',
                    headerName: 'Booked',
                    width: 80,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const value = params.value || 0;
                        const config = COLUMN_CONFIG.consultationsBooked;
                        return (
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'consultationsBooked'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                    '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'consultationsAttended',
                    headerName: 'Attended',
                    width: 90,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const value = params.value || 0;
                        const config = COLUMN_CONFIG.consultationsAttended;
                        return (
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'consultationsAttended'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                    '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'conversions',
                    headerName: 'Converted',
                    width: 95,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const value = params.value || 0;
                        const config = COLUMN_CONFIG.conversions;
                        return (
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'conversions'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                    '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'revenue',
                    headerName: 'Revenue',
                    width: 100,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const value = params.value || 0;
                        const config = COLUMN_CONFIG.revenue;
                        return (
                            <Chip
                                label={formatCurrency(value)}
                                size="small"
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'showUpRate',
                    headerName: 'Show-up',
                    width: 100,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const rate = parseFloat(params.value) || 0;
                        const getShowUpColor = (r) => {
                            if (r >= 80) return COLUMN_CONFIG.showUpRate.good;
                            if (r >= 60) return COLUMN_CONFIG.showUpRate.medium;
                            return COLUMN_CONFIG.showUpRate.low;
                        };
                        const color = getShowUpColor(rate);
                        return (
                            <Chip
                                label={`${rate}%`}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        );
                    },
                },
                {
                    field: 'conversionRate',
                    headerName: 'Conv. Rate',
                    width: 110,
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => {
                        const rate = parseFloat(params.value) || 0;
                        const getConvColor = (r) => {
                            if (r >= 50) return COLUMN_CONFIG.conversionRate.good;
                            if (r >= 30) return COLUMN_CONFIG.conversionRate.medium;
                            return COLUMN_CONFIG.conversionRate.low;
                        };
                        const color = getConvColor(rate);
                        return (
                            <Chip
                                label={`${rate}%`}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        );
                    },
                },
            ];
        }

        // Basic columns (fallback for old data structure) - using Chip style
        return [
            ...baseColumns,
            {
                field: 'consultations',
                headerName: 'Consultations',
                flex: 1,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    const config = COLUMN_CONFIG.consultations;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'consultations'); } : undefined}
                            sx={{
                                bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                color: value > 0 ? config.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                            }}
                        />
                    );
                },
            },
            {
                field: 'conversions',
                headerName: 'Conversions',
                flex: 1,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    const config = COLUMN_CONFIG.conversions;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'conversions'); } : undefined}
                            sx={{
                                bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                color: value > 0 ? config.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 && practiceId ? 'pointer' : 'default',
                                '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                            }}
                        />
                    );
                },
            },
            {
                field: 'revenue',
                headerName: 'Revenue',
                flex: 1,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    const config = COLUMN_CONFIG.revenue;
                    return (
                        <Chip
                            label={formatCurrency(value)}
                            size="small"
                            sx={{
                                bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                color: value > 0 ? config.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 50,
                            }}
                        />
                    );
                },
            },
            {
                field: 'conversionRate',
                headerName: 'Conversion Rate',
                flex: 1,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const rate = parseFloat(params.value) || 0;
                    const getConvColor = (r) => {
                        if (r >= 50) return COLUMN_CONFIG.conversionRate.good;
                        if (r >= 30) return COLUMN_CONFIG.conversionRate.medium;
                        return COLUMN_CONFIG.conversionRate.low;
                    };
                    const color = getConvColor(rate);
                    return (
                        <Chip
                            label={`${rate}%`}
                            size="small"
                            sx={{
                                bgcolor: alpha(color, 0.12),
                                color,
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 50,
                            }}
                        />
                    );
                },
            },
        ];
    }, [processedData, practiceId, handleCellClick]);

    // Single source of truth for the show/hide menu AND the CSV export: the
    // columns the grid actually renders (which differ between enhanced and
    // basic data modes). Deriving from `columns` guarantees the export always
    // contains exactly the columns the user sees — no phantom/missing columns.
    const currentColumns = useMemo(
        () => columns.map((c) => ({ field: c.field, label: c.headerName })),
        [columns]
    );
    const visibleColumns = useMemo(
        () => currentColumns.filter((c) => !hiddenColumns.includes(c.field)),
        [currentColumns, hiddenColumns]
    );

    // Export CSV — mirrors the visible columns exactly.
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const headers = visibleColumns.map((c) => c.label);
        const rows = filteredData.map((row) => visibleColumns.map((col) => {
            if (col.field === 'revenue') return formatCurrency(row.revenue || 0);
            if (col.field === 'showUpRate') return `${parseFloat(row.showUpRate) || 0}%`;
            if (col.field === 'conversionRate') return `${parseFloat(row.conversionRate) || 0}%`;
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('team-members', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, visibleColumns, buildExportName, dateRange]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            {/* Header with Search */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Tooltip title="Performance metrics for each team member." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Table / Graph toggle */}
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        size="small"
                        onChange={(_, newView) => { if (newView) setView(newView); }}
                        sx={{ height: 32 }}
                    >
                        <ToggleButton
                            value="table"
                            sx={{
                                px: 1.5,
                                fontSize: '12px',
                                fontWeight: 600,
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px 0 0 8px',
                                '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                            }}
                        >
                            <Iconify icon="eva:grid-outline" width={14} sx={{ mr: 0.5 }} />
                            Table
                        </ToggleButton>
                        <ToggleButton
                            value="graph"
                            sx={{
                                px: 1.5,
                                fontSize: '12px',
                                fontWeight: 600,
                                border: '1px solid #e2e8f0',
                                borderRadius: '0 8px 8px 0',
                                '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                            }}
                        >
                            <Iconify icon="eva:bar-chart-outline" width={14} sx={{ mr: 0.5 }} />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {showSearch && (
                        <TextField
                            size="small"
                            placeholder="Search team member..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ width: 250 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {currentColumns.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* DataGrid — shown only in table view */}
            {view === 'table' && (
                <Box>
                    <DataGridPro
                        rows={filteredData}
                        columns={columns}
                        autoHeight
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        disableRowSelectionOnClick
                        disableColumnMenu={false}
                        pagination
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        getRowId={(row) => row.id ?? row.name ?? `row-${Math.random()}`}
                        onRowClick={onPractitionerClick ? (params) => onPractitionerClick(params.row) : undefined}
                        columnVisibilityModel={columnVisibilityModel}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f1f5f9',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: '#f8fafc',
                                cursor: onPractitionerClick ? 'pointer' : 'default',
                            },
                        }}
                    />
                </Box>
            )}

            {view === 'table' && filteredData.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No team member data available
                    </Typography>
                </Box>
            )}

            {/* Bar chart — shown only in graph view */}
            {view === 'graph' && (
                <Box sx={{ p: 2.5 }}>
                    <MetricLineChart
                        type="bar"
                        categories={chartData.categories}
                        series={chartData.series}
                        height={340}
                        yAxisTitle="Count"
                    />
                </Box>
            )}

            {/* Lead detail popup */}
            <SourceLeadPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={practiceId}
                status={popupConfig.status}
                statusLabel={popupConfig.statusLabel}
                dateRange={dateRange}
                title={popupConfig.title}
                assignedTo={popupConfig.assignedTo}
            />
        </Box>
    );
}

PractitionersTable.propTypes = {
    practitioners: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            avatar: PropTypes.string,
            conversions: PropTypes.number,
            consultations: PropTypes.number,
            revenue: PropTypes.number,
            conversionRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    title: PropTypes.string,
    showSearch: PropTypes.bool,
    onPractitionerClick: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
};

PractitionersTable.defaultProps = {
    title: 'All Team Members',
    showSearch: true,
    onPractitionerClick: null,
    practiceId: null,
    dateRange: null,
};
